/**
 * Word-level diff between what was said and what the machine wrote down.
 *
 * Composition §5: the before/after of normalization is `evidence.excerpt` → `claim_text`.
 * `normalization.changed` reports something else entirely and must not be used here.
 */

export type DiffOp = 'same' | 'removed' | 'added';

export type DiffToken = {
  op: DiffOp;
  text: string;
};

/** Compares on a normalized key so casing and punctuation do not register as edits, while the
 *  rendered token keeps its original form. */
function comparisonKey(word: string): string {
  return word
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^\p{L}\p{N}]/gu, '');
}

function tokenize(text: string): string[] {
  return text.split(/\s+/).filter(word => word.length > 0);
}

/**
 * Longest common subsequence over word keys. Bounded at `MAX_WORDS` per side because this runs
 * once per ledger row and a transcript excerpt is a paragraph, not a document.
 */
const MAX_WORDS = 400;

export function diffWords(before: string, after: string): { before: DiffToken[]; after: DiffToken[] } {
  const beforeWords = tokenize(before).slice(0, MAX_WORDS);
  const afterWords = tokenize(after).slice(0, MAX_WORDS);
  const beforeKeys = beforeWords.map(comparisonKey);
  const afterKeys = afterWords.map(comparisonKey);

  // table[i][j] = LCS length of beforeKeys[i..] and afterKeys[j..]
  const table: number[][] = Array.from({ length: beforeKeys.length + 1 }, () =>
    new Array<number>(afterKeys.length + 1).fill(0)
  );
  for (let i = beforeKeys.length - 1; i >= 0; i -= 1) {
    for (let j = afterKeys.length - 1; j >= 0; j -= 1) {
      table[i]![j] =
        beforeKeys[i] === afterKeys[j] ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }

  const beforeTokens: DiffToken[] = [];
  const afterTokens: DiffToken[] = [];
  let i = 0;
  let j = 0;

  while (i < beforeKeys.length && j < afterKeys.length) {
    if (beforeKeys[i] === afterKeys[j]) {
      beforeTokens.push({ op: 'same', text: beforeWords[i]! });
      afterTokens.push({ op: 'same', text: afterWords[j]! });
      i += 1;
      j += 1;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      beforeTokens.push({ op: 'removed', text: beforeWords[i]! });
      i += 1;
    } else {
      afterTokens.push({ op: 'added', text: afterWords[j]! });
      j += 1;
    }
  }
  while (i < beforeKeys.length) beforeTokens.push({ op: 'removed', text: beforeWords[i++]! });
  while (j < afterKeys.length) afterTokens.push({ op: 'added', text: afterWords[j++]! });

  return { before: mergeRuns(beforeTokens), after: mergeRuns(afterTokens) };
}

/** Collapses adjacent same-op tokens into one span so the DOM has runs, not one node per word. */
function mergeRuns(tokens: DiffToken[]): DiffToken[] {
  const merged: DiffToken[] = [];
  for (const token of tokens) {
    const last = merged[merged.length - 1];
    if (last && last.op === token.op) last.text = `${last.text} ${token.text}`;
    else merged.push({ ...token });
  }
  return merged;
}

/**
 * How much of the spoken text survived, 0–1. Composition §5: a claim compressed past ~70% is where
 * distortion lives, and an editor should be able to sort by it.
 */
export function compressionRatio(before: string, after: string): number {
  const beforeLength = tokenize(before).length;
  if (beforeLength === 0) return 0;
  return Math.min(tokenize(after).length / beforeLength, 1);
}
